import { supabase } from '../utils/supabase';

/**
 * API service functions for squad operations
 * These replace the Next.js API routes and call Supabase directly
 */

// Squad configuration constants
const SQUAD_MAX_SIZE = 5;

/**
 * Get squad details
 */
export const getSquadDetails = async (squadId) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    // Fetch squad details with squad_number using RPC
    const { data: squadData, error: squadError } = await supabase.rpc(
      'get_squad_details',
      { p_squad_id: squadId }
    );

    if (squadError) {
      throw new Error(squadError.message || 'Failed to fetch squad details');
    }

    if (!squadData || squadData.length === 0) {
      return null;
    }

    const squad = squadData[0];

    // Fetch squad info from squads_public view
    const { data: squadInfo } = await supabase
      .from('squads_public')
      .select('meetup_point, meetup_time, notes')
      .eq('id', squadId)
      .single();

    // Fetch squad members
    const { data: membersData, error: membersError } = await supabase.rpc(
      'get_squad_members',
      { p_squad_id: squadId }
    );

    if (membersError) {
      throw new Error(membersError.message || 'Failed to fetch squad members');
    }

    // Find host name
    const hostMember = membersData?.find((m) => m.is_host === true);
    const hostName = hostMember?.attendee_name || null;

    return {
      squad_id: squad.squad_id,
      event_id: squad.event_id,
      event_name: squad.event_name,
      squad_number: squad.squad_number,
      member_count: squad.member_count,
      squad_status: squad.squad_status,
      meetup_point: squadInfo?.meetup_point || null,
      meetup_time: squadInfo?.meetup_time || null,
      notes: squadInfo?.notes || null,
      members: membersData || [],
      host_name: hostName,
    };
  } catch (error) {
    console.error('Error fetching squad details:', error);
    throw error;
  }
};

/**
 * Join a squad
 */
export const joinSquad = async ({ eventId, eventName, squadId, attendeeUuid, attendeeName, introText, firstTimeVenue, accessToken }) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    // Create authenticated client if access token is provided
    let client = supabase;
    if (accessToken) {
      // Note: In a real implementation, you'd create a new client with the token
      // For now, we'll use the existing client and rely on RLS
      client = supabase;
    }

    const { data, error } = await client.rpc('join_squad', {
      p_event_id: eventId,
      p_event_name: eventName,
      p_requested_squad_id: squadId || null,
      p_attendee_uuid: attendeeUuid,
      p_attendee_name: attendeeName,
      p_intro_text: introText,
      p_first_time_venue: firstTimeVenue,
    });

    if (error) {
      throw new Error(error.message || 'Failed to join squad');
    }

    return {
      squadId: data,
      attendeeUuid: attendeeUuid,
    };
  } catch (error) {
    console.error('Error joining squad:', error);
    throw error;
  }
};

/**
 * Create a squad
 */
export const createSquad = async ({ eventId, eventName, squadId, createAuthToken }) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    let authTokenForResponse = null;
    let userIdForResponse = null;

    // Try to create anonymous auth if requested
    if (createAuthToken) {
      const { data: authData, error: authError } = await supabase.auth.signInAnonymously();
      
      if (!authError && authData.session) {
        authTokenForResponse = authData.session.access_token;
        userIdForResponse = authData.user.id;
      }
    }

    // Create the squad
    const squadData = {
      event_id: eventId,
      event_name: eventName,
      status: 'open',
      member_count: 0,
    };

    if (squadId) {
      squadData.id = squadId;
    }

    const { data, error } = await supabase
      .from('squads')
      .insert(squadData)
      .select()
      .single();

    if (error) {
      // If squad already exists, return it
      if (error.code === '23505') {
        const { data: existingSquad } = await supabase
          .from('squads')
          .select()
          .eq('id', squadId || data?.id)
          .single();

        if (existingSquad) {
          return {
            eventId: existingSquad.event_id,
            squadId: existingSquad.id,
            eventName: existingSquad.event_name,
            ...(authTokenForResponse && { accessToken: authTokenForResponse, userId: userIdForResponse }),
          };
        }
      }
      throw new Error(error.message || 'Failed to create squad');
    }

    return {
      eventId: data.event_id,
      squadId: data.id,
      eventName: data.event_name,
      ...(authTokenForResponse && { accessToken: authTokenForResponse, userId: userIdForResponse }),
    };
  } catch (error) {
    console.error('Error creating squad:', error);
    throw error;
  }
};

/**
 * Send a message to a squad
 */
export const sendSquadMessage = async ({ squad_id, attendee_uuid, attendee_name, message }) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    // Convert empty UUID string to null
    const attendeeUuidForRpc = attendee_uuid?.trim() === '' ? null : attendee_uuid;

    const { error } = await supabase.rpc('send_squad_message', {
      p_squad_id: squad_id,
      p_attendee_uuid: attendeeUuidForRpc,
      p_attendee_name: attendee_name.trim(),
      p_message: message.trim(),
    });

    if (error) {
      throw new Error(error.message || 'Failed to send message');
    }

    return { ok: true };
  } catch (error) {
    console.error('Error sending message:', error);
    throw error;
  }
};

/**
 * Send a welcome message to the squad when a user joins.
 * Uses the joiner's attendee_uuid so RLS allows the insert (only squad members can send);
 * display name remains 'CrowdOne' so the UI shows it as a system message.
 */
export const sendWelcomeMessage = async ({ squad_id, attendee_uuid, attendee_name, intro_text, first_time_venue, member_count, event_name }) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    // Extract venue name from event name (assumes format like "Artist @ Venue")
    const venueName = event_name.includes('@') 
      ? event_name.split('@')[1].trim() 
      : event_name;

    // Build profile section
    let profileSection = '';
    if (intro_text || first_time_venue !== null) {
      profileSection = '\n\nAbout them:';
      if (intro_text) {
        profileSection += `\n"${intro_text}"`;
      }
      if (first_time_venue !== null) {
        profileSection += `\nFirst time at ${venueName}? ${first_time_venue ? 'Yes' : 'No'}`;
      }
    }

    // Construct welcome message
    const welcomeMessage = `🎵 ${attendee_name} joined the squad!${profileSection}

You're person ${member_count}/${SQUAD_MAX_SIZE}. Once we hit ${SQUAD_MAX_SIZE} people, we'll coordinate the meetup.

Say hi! 👋`;

    const { error } = await supabase.rpc('send_squad_message', {
      p_squad_id: squad_id,
      p_attendee_uuid: attendee_uuid || null,
      p_attendee_name: 'CrowdOne',
      p_message: welcomeMessage,
    });

    if (error) {
      throw new Error(error.message || 'Failed to send welcome message');
    }

    return { ok: true };
  } catch (error) {
    console.error('Error sending welcome message:', error);
    // Don't throw - welcome message is nice-to-have, not critical
    return { ok: false, error };
  }
};

/**
 * Send a pre-show reminder message to the squad.
 * This should be called 4 hours before the event starts.
 * Message includes event details and meetup confirmation.
 */
export const sendPreShowReminder = async ({ 
  squad_id, 
  event_name, 
  event_time, 
  venue_name, 
  venue_address, 
  meetup_plan 
}) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    // Check if squad has 2+ members before sending
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('member_count')
      .eq('id', squad_id)
      .single();

    if (squadError) {
      throw new Error(squadError.message || 'Failed to fetch squad details');
    }

    if (!squadData || squadData.member_count < 2) {
      console.log(`Squad ${squad_id} has less than 2 members, skipping pre-show reminder`);
      return { ok: false, reason: 'insufficient_members' };
    }

    // Format the meetup plan
    const meetupPlanText = meetup_plan || '30 min before doors';

    // Construct pre-show reminder message
    const reminderMessage = `🚨 TONIGHT! ${event_name}

⏰ Doors: ${event_time}
📍 Location: ${venue_address}

Squad meetup plan: ${meetupPlanText}

Who's confirmed? React with 👋 if you're coming!

See you soon! 🎵`;

    // Send message as system message (CrowdOne)
    // Use null attendee_uuid to make it a true system message
    const { error } = await supabase.rpc('send_squad_message', {
      p_squad_id: squad_id,
      p_attendee_uuid: null,
      p_attendee_name: 'CrowdOne',
      p_message: reminderMessage,
    });

    if (error) {
      throw new Error(error.message || 'Failed to send pre-show reminder');
    }

    return { ok: true };
  } catch (error) {
    console.error('Error sending pre-show reminder:', error);
    throw error;
  }
};

/**
 * Find squad by email and event ID
 */
export const findMySquad = async ({ email, eventId }) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    const normalizedEmail = decodeURIComponent(email).toLowerCase().trim();

    let query = supabase
      .from('ticket_tailor_orders')
      .select('squad_id, event_id, skipped, order_id, email')
      .eq('email', normalizedEmail)
      .eq('skipped', false)
      .not('squad_id', 'is', null);

    if (eventId) {
      query = query.eq('event_id', eventId);
    }

    const { data: orders, error } = await query.order('created_at', { ascending: false }).limit(1);

    if (error) {
      throw new Error(error.message || 'Failed to find squad');
    }

    if (!orders || orders.length === 0 || !orders[0].squad_id) {
      // Try without event_id filter
      if (eventId) {
        const { data: ordersWithoutEvent } = await supabase
          .from('ticket_tailor_orders')
          .select('squad_id, event_id, skipped, order_id, email')
          .eq('email', normalizedEmail)
          .eq('skipped', false)
          .not('squad_id', 'is', null)
          .order('created_at', { ascending: false })
          .limit(1);

        if (ordersWithoutEvent && ordersWithoutEvent.length > 0 && ordersWithoutEvent[0].squad_id) {
          return {
            squadId: ordersWithoutEvent[0].squad_id,
            eventId: ordersWithoutEvent[0].event_id,
          };
        }
      }

      return null;
    }

    return {
      squadId: orders[0].squad_id,
      eventId: orders[0].event_id,
    };
  } catch (error) {
    console.error('Error finding squad:', error);
    throw error;
  }
};

/**
 * Send a post-show survey message to the squad.
 * This should be called 24 hours after the event ends.
 * Message includes survey link to gather feedback.
 */
export const sendPostShowSurvey = async ({ squad_id, event_name }) => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    // Check if squad has 2+ members before sending
    const { data: squadData, error: squadError } = await supabase
      .from('squads')
      .select('member_count')
      .eq('id', squad_id)
      .single();

    if (squadError) {
      throw new Error(squadError.message || 'Failed to fetch squad details');
    }

    if (!squadData || squadData.member_count < 2) {
      console.log(`Squad ${squad_id} has less than 2 members, skipping post-show survey`);
      return { ok: false, reason: 'insufficient_members' };
    }

    // Survey link - update this to your actual survey URL
    const surveyLink = 'https://crowdone.app/survey';

    // Construct post-show survey message
    const surveyMessage = `Hey squad! 👋

How was ${event_name} last night?

Quick 30-second survey to help us improve:

1. Did you meet up with your squad? (Yes/No)
2. If yes, how was the experience? (⭐ 1-5 stars)
3. Would you join a squad for another show? (Yes/Maybe/No)
4. Any feedback or suggestions?

📋 Take the survey: ${surveyLink}

Thanks for using CrowdOne! 🎵`;

    // Send message as system message (CrowdOne)
    // Use null attendee_uuid to make it a true system message
    const { error } = await supabase.rpc('send_squad_message', {
      p_squad_id: squad_id,
      p_attendee_uuid: null,
      p_attendee_name: 'CrowdOne',
      p_message: surveyMessage,
    });

    if (error) {
      throw new Error(error.message || 'Failed to send post-show survey');
    }

    return { ok: true };
  } catch (error) {
    console.error('Error sending post-show survey:', error);
    throw error;
  }
};

/**
 * Create anonymous auth token
 */
export const createAnonymousAuth = async () => {
  if (!supabase) {
    throw new Error('Supabase not configured');
  }

  try {
    const { data, error } = await supabase.auth.signInAnonymously();

    if (error) {
      throw new Error(error.message || 'Failed to create anonymous auth');
    }

    return {
      accessToken: data.session.access_token,
      userId: data.user.id,
    };
  } catch (error) {
    console.error('Error creating anonymous auth:', error);
    throw error;
  }
};
